import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";
// Dérivé d'API_BASE_URL (configurable) plutôt que codé en dur sur localhost :
// les documents/logo de la société étaient introuvables (404 silencieux) dès
// que l'app tournait sur un autre domaine que localhost.
const UPLOADS_URL = `${API_BASE_URL.replace(/\/+$/, "")}/uploads`;

const SocieteDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [societe, setSociete] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [currentProfile, setCurrentProfile] = useState(null);
  const [currentSocieteId, setCurrentSocieteId] = useState(null);
  const [imageModal, setImageModal] = useState({
    show: false,
    title: "",
    url: ""
  });
  const [documentChecking, setDocumentChecking] = useState({
    nif: false,
    rccm: false
  });

  // Récupérer les infos de l'utilisateur connecté
  useEffect(() => {
    const userStr = localStorage.getItem("user");
    if (userStr) {
      try {
        const userData = JSON.parse(userStr);
        setCurrentProfile(String(userData.profile));
        setCurrentSocieteId(Number(userData.societe_id));
      } catch (e) {
        console.error("Erreur parse user:", e);
      }
    }
  }, []);

  // Fonction pour charger les détails de la société
  const fetchSocieteDetails = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        return;
      }

      const response = await axios.get(`${API_BASE_URL}/societes/get.php?id=${id}`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });

      if (response.data.success) {
        setSociete(response.data.societe);
      } else {
        setError(response.data.message || "Société non trouvée.");
      }
    } catch (err) {
      console.error("Erreur:", err);
      
      if (err.response) {
        const { status } = err.response;
        
        if (status === 401) {
          setError("Session expirée. Veuillez vous reconnecter.");
          setTimeout(() => navigate("/login"), 2000);
        } else if (status === 403) {
          setError("Accès interdit. Vous ne pouvez consulter que votre propre société.");
        } else if (status === 404) {
          setError("Société non trouvée.");
        } else {
          setError(`Erreur ${status}: Impossible de charger les détails.`);
        }
      } else if (err.request) {
        setError("Serveur inaccessible. Vérifiez votre connexion.");
      } else {
        setError("Erreur de chargement.");
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchSocieteDetails();
  }, [fetchSocieteDetails]);

  // Fonction pour vérifier si un fichier existe
  const checkFileExists = async (url) => {
    try {
      const response = await fetch(url, { 
        method: 'HEAD',
        mode: 'cors'
      });
      return response.ok;
    } catch (error) {
      return false;
    }
  };

  // Fonction pour afficher un document
  const showImage = async (title, documentType) => {
    if (!societe) {
      alert("Informations de la société non chargées.");
      return;
    }
    
    setDocumentChecking(prev => ({ ...prev, [documentType.toLowerCase()]: true }));
    
    try {
      let documentUrl = null;
      let found = false;
      
      const dbPath = societe[`chemin_${documentType.toLowerCase()}`];
      if (dbPath) {
        documentUrl = `${UPLOADS_URL}/${dbPath}`;
        found = await checkFileExists(documentUrl);
      }
      
      if (!found) {
        const extensions = ['.png', '.jpg', '.jpeg', '.pdf', '.PNG', '.JPG', '.JPEG', '.PDF'];
        for (const ext of extensions) {
          documentUrl = `${UPLOADS_URL}/societe/${documentType.toLowerCase()}_${societe.id_societe}${ext}`;
          if (await checkFileExists(documentUrl)) {
            found = true;
            break;
          }
        }
      }
      
      if (!found) {
        const extensions = ['.png', '.jpg', '.jpeg', '.pdf', '.PNG', '.JPG', '.JPEG', '.PDF'];
        for (const ext of extensions) {
          documentUrl = `${UPLOADS_URL}/${documentType.toLowerCase()}_${societe.id_societe}${ext}`;
          if (await checkFileExists(documentUrl)) {
            found = true;
            break;
          }
        }
      }
      
      if (!found && societe[`${documentType.toLowerCase()}_societe`]) {
        const docNumber = societe[`${documentType.toLowerCase()}_societe`];
        const extensions = ['.png', '.jpg', '.jpeg', '.pdf', '.PNG', '.JPG', '.JPEG', '.PDF'];
        for (const ext of extensions) {
          documentUrl = `${UPLOADS_URL}/${docNumber}${ext}`;
          if (await checkFileExists(documentUrl)) {
            found = true;
            break;
          }
        }
      }
      
      if (found && documentUrl) {
        setImageModal({
          show: true,
          title: `${title} - ${societe.nom_societe}`,
          url: documentUrl
        });
      } else {
        alert(`${title} non disponible pour cette société.`);
      }
    } catch (error) {
      console.error("Erreur lors de la recherche du document:", error);
      alert("Erreur lors de l'accès au document.");
    } finally {
      setDocumentChecking(prev => ({ ...prev, [documentType.toLowerCase()]: false }));
    }
  };

  // Fermer le modal
  const closeModal = () => {
    setImageModal({
      show: false,
      title: "",
      url: ""
    });
  };

  // Fonction pour activer/désactiver la société
  const handleChangeStatus = async () => {
    if (!societe) return;
    
    if (societe.id_societe === currentSocieteId) {
      alert("Vous ne pouvez pas modifier le statut de votre propre société.");
      return;
    }

    if (currentProfile !== "1") {
      alert("Seul un administrateur peut modifier le statut d'une société.");
      return;
    }

    const newStatus = societe.statut_societe === 1 ? 0 : 1;
    const actionText = newStatus === 1 ? "activation" : "désactivation";
    
    if (!window.confirm(`Confirmer l'${actionText} de la société "${societe.nom_societe}" ?`)) {
      return;
    }

    setActionLoading(true);
    setError("");
    setSuccessMsg("");
    
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        return;
      }
      
      const response = await fetch(`${API_BASE_URL}/societes/change_status.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_societe: societe.id_societe,
          statut_societe: newStatus
        })
      });
      
      const text = await response.text();
      
      const jsonMatch = text.match(/\{.*\}/s);
      let data;
      
      if (jsonMatch) {
        try {
          data = JSON.parse(jsonMatch[0]);
        } catch (parseError) {
          const cleanedText = text.replace(/<[^>]*>/g, '').trim();
          const lastBrace = cleanedText.lastIndexOf('}');
          if (lastBrace !== -1) {
            const jsonText = cleanedText.substring(0, lastBrace + 1);
            data = JSON.parse(jsonText);
          }
        }
      } else {
        throw new Error("Réponse invalide du serveur");
      }
      
      if (data && data.success === true) {
        const result = newStatus === 1 ? "activée" : "désactivée";
        setSuccessMsg(`Société "${societe.nom_societe}" ${result} avec succès.`);
        
        fetchSocieteDetails();
      } else {
        setError(data?.message || "Échec de l'opération.");
      }
    } catch (err) {
      console.error("Erreur complète:", err);
      setError("Erreur lors du changement de statut.");
    } finally {
      setActionLoading(false);
    }
  };

  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return "Non disponible";
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Obtenir le badge de statut
  const getStatutBadge = (statut) => {
    if (statut === 1) {
      return <span className="badge" style={{ backgroundColor: '#198754', color: 'white', fontSize: '0.75rem', padding: '4px 8px' }}>Actif</span>;
    } else {
      return <span className="badge" style={{ backgroundColor: '#6c757d', color: 'white', fontSize: '0.75rem', padding: '4px 8px' }}>Inactif</span>;
    }
  };

  // Vérifier les permissions
  const canEdit = () => {
    if (currentProfile === "1") return true;
    if (currentProfile === "3" && societe?.id_societe === currentSocieteId) return true;
    return false;
  };

  const canChangeStatus = () => {
    return currentProfile === "1" && societe?.id_societe !== currentSocieteId;
  };

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: '#0B3C5D', fontWeight: '600' }}>Détails de la société</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <button 
                  className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                  onClick={() => navigate("/societes")}
                  style={{ color: '#1E88C8', textAlign: 'left' }}
                >
                  Sociétés
                </button>
              </li>
              <li className="breadcrumb-item active" style={{ color: '#757575' }}>Chargement...</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body text-center py-5">
                  <div className="spinner-border" role="status" style={{ color: '#0B3C5D' }}>
                    <span className="visually-hidden">Chargement...</span>
                  </div>
                  <p className="mt-3" style={{ color: '#2E2E2E' }}>Chargement des détails...</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (!societe) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: '#0B3C5D', fontWeight: '600' }}>Détails de la société</h1>
          <nav>
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <button 
                  className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                  onClick={() => navigate("/societes")}
                  style={{ color: '#1E88C8', textAlign: 'left' }}
                >
                  Sociétés
                </button>
              </li>
              <li className="breadcrumb-item active" style={{ color: '#f44336' }}>Erreur</li>
            </ol>
          </nav>
        </div>
        
        <section className="section">
          <div className="row">
            <div className="col-lg-12">
              <div className="card" style={{ 
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                border: 'none'
              }}>
                <div className="card-body">
                  <div className="alert alert-danger alert-dismissible fade show" style={{ 
                    backgroundColor: '#ffebee',
                    borderColor: '#f44336',
                    color: '#c62828',
                    borderRadius: '8px',
                    borderLeft: '4px solid #f44336'
                  }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      <div className="flex-grow-1">{error || "Société non trouvée"}</div>
                      <button 
                        type="button" 
                        className="btn-close" 
                        onClick={() => navigate("/societes")}
                      ></button>
                    </div>
                  </div>
                  <div className="text-center mt-3">
                    <button 
                      className="btn"
                      onClick={() => navigate("/societes")}
                      style={{
                        color: '#0B3C5D',
                        borderColor: '#0B3C5D',
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 24px',
                        fontWeight: '500',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#0B3C5D';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#0B3C5D';
                      }}
                    >
                      <i className="bi bi-arrow-left me-2"></i>
                      Retour à la liste
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ color: '#0B3C5D', fontWeight: '600' }}>Détails de la société</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <button 
                className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                onClick={() => navigate("/dashboard")}
                style={{ color: '#1E88C8', textAlign: 'left' }}
              >
                Dashboard
              </button>
            </li>
            <li className="breadcrumb-item">
              <button 
                className="btn btn-link p-0 border-0 bg-transparent text-decoration-none"
                onClick={() => navigate("/societes")}
                style={{ color: '#1E88C8', textAlign: 'left' }}
              >
                Sociétés
              </button>
            </li>
            <li className="breadcrumb-item active" style={{ color: '#0B3C5D' }}>
              {societe.nom_societe}
            </li>
          </ol>
        </nav>
      </div>

      <section className="section">
        {successMsg && (
          <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
            backgroundColor: '#e8f5e9',
            borderColor: '#4caf50',
            color: '#2e7d32',
            borderRadius: '8px',
            borderLeft: '4px solid #4caf50'
          }}>
            <div className="d-flex align-items-center">
              <i className="bi bi-check-circle me-2"></i>
              <div className="flex-grow-1">{successMsg}</div>
              <button type="button" className="btn-close" onClick={() => setSuccessMsg("")}></button>
            </div>
          </div>
        )}

        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
            backgroundColor: '#ffebee',
            borderColor: '#f44336',
            color: '#c62828',
            borderRadius: '8px',
            borderLeft: '4px solid #f44336'
          }}>
            <div className="d-flex align-items-center">
              <i className="bi bi-exclamation-triangle me-2"></i>
              <div className="flex-grow-1">{error}</div>
              <button type="button" className="btn-close" onClick={() => setError("")}></button>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-lg-4">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <div className="text-center mb-4">
                  <div className="mb-3">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center" 
                         style={{ 
                          width: "80px", 
                          height: "80px",
                          backgroundColor: '#E3F2FD',
                          color: '#0B3C5D'
                        }}>
                      <i className="bi bi-building fs-2"></i>
                    </div>
                  </div>
                  
                  <h4 style={{ color: '#0B3C5D', marginBottom: '8px' }}>{societe.nom_societe}</h4>
                  <p className="mb-3" style={{ color: '#1E88C8' }}>{societe.email_societe}</p>
                  
                  <div className="mb-3">
                    {getStatutBadge(societe.statut_societe)}
                  </div>
                </div>

                <hr className="my-4" style={{ borderColor: '#F5F7FA' }} />
                <h6 style={{ color: '#0B3C5D', fontWeight: '600', marginBottom: '16px' }}>
                  <i className="bi bi-gear me-2" style={{ color: '#1E88C8' }}></i>
                  Actions
                </h6>
                
                <div className="d-flex justify-content-center gap-3 mb-4">
                  {canEdit() && (
                    <button 
                      className="btn rounded-circle d-flex align-items-center justify-content-center"
                      onClick={() => navigate(`/societes/edit/${societe.id_societe}`)}
                      disabled={actionLoading}
                      title="Modifier"
                      style={{ 
                        width: "50px", 
                        height: "50px",
                        color: '#0B3C5D',
                        borderColor: '#0B3C5D',
                        backgroundColor: 'transparent',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#0B3C5D';
                        e.target.style.color = 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = '#0B3C5D';
                      }}
                    >
                      <i className="bi bi-pencil"></i>
                    </button>
                  )}
                  
                  {canChangeStatus() && (
                    <button 
                      className="btn rounded-circle d-flex align-items-center justify-content-center"
                      onClick={handleChangeStatus}
                      disabled={actionLoading}
                      title={societe.statut_societe === 1 ? "Désactiver" : "Activer"}
                      style={{ 
                        width: "50px", 
                        height: "50px",
                        color: societe.statut_societe === 1 ? '#ffc107' : '#198754',
                        borderColor: societe.statut_societe === 1 ? '#ffc107' : '#198754',
                        backgroundColor: 'transparent',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = societe.statut_societe === 1 ? '#ffc107' : '#198754';
                        e.target.style.color = societe.statut_societe === 1 ? '#2E2E2E' : 'white';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = societe.statut_societe === 1 ? '#ffc107' : '#198754';
                      }}
                    >
                      {actionLoading ? (
                        <span className="spinner-border spinner-border-sm"></span>
                      ) : (
                        <i className={`bi ${societe.statut_societe === 1 ? 'bi-toggle-off' : 'bi-toggle-on'}`}></i>
                      )}
                    </button>
                  )}
                  
                  <button 
                    className="btn rounded-circle d-flex align-items-center justify-content-center"
                    onClick={() => navigate(`/societes/${societe.id_societe}/users`)}
                    title="Voir les utilisateurs"
                    style={{ 
                      width: "50px", 
                      height: "50px",
                      color: '#4FC3F7',
                      borderColor: '#4FC3F7',
                      backgroundColor: 'transparent',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#4FC3F7';
                      e.target.style.color = '#2E2E2E';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.color = '#4FC3F7';
                    }}
                  >
                    <i className="bi bi-people"></i>
                  </button>
                </div>
                
                <div className="d-grid">
                  <button 
                    className="btn"
                    onClick={() => navigate("/societes")}
                    disabled={actionLoading}
                    style={{
                      color: '#2E2E2E',
                      borderColor: '#e0e0e0',
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 16px',
                      fontWeight: '500',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#F5F7FA';
                      e.target.style.borderColor = '#0B3C5D';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = '#e0e0e0';
                    }}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Retour à la liste
                  </button>
                </div>
              </div>
            </div>

            {/* Carte Documents */}
            <div className="card mt-3" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title" style={{ 
                  color: '#0B3C5D',
                  borderBottom: '2px solid #F5F7FA',
                  paddingBottom: '12px',
                  marginBottom: '20px'
                }}>
                  <i className="bi bi-folder me-2" style={{ color: '#1E88C8' }}></i>
                  Documents
                </h5>
                
                <div className="row g-2">
                  {/* Document NIF */}
                  <div className="col-6">
                    <div 
                      className="card text-center border cursor-pointer"
                      onClick={() => showImage("NIF", "NIF")}
                      style={{ 
                        minHeight: "120px",
                        borderColor: '#e0e0e0',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#0B3C5D';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e0e0e0';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div className="card-body d-flex flex-column justify-content-center p-3">
                        {documentChecking.nif ? (
                          <>
                            <div className="spinner-border spinner-border-sm mb-2" style={{ color: '#0B3C5D' }}></div>
                            <small style={{ color: '#757575' }}>Chargement...</small>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-file-earmark-text fs-2 mb-2" style={{ color: '#0B3C5D' }}></i>
                            <div>
                              <small style={{ color: '#757575', fontSize: '0.8rem' }}>NIF</small>
                              <div className="mt-1">
                                <span className="badge" style={{ 
                                  backgroundColor: '#0B3C5D', 
                                  color: 'white',
                                  fontSize: '0.7rem',
                                  padding: '2px 6px'
                                }}>
                                  {societe.nif_societe || "Non défini"}
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Document RCCM */}
                  <div className="col-6">
                    <div 
                      className="card text-center border cursor-pointer"
                      onClick={() => showImage("RCCM", "RCCM")}
                      style={{ 
                        minHeight: "120px",
                        borderColor: '#e0e0e0',
                        borderRadius: '8px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.borderColor = '#0B3C5D';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.borderColor = '#e0e0e0';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    >
                      <div className="card-body d-flex flex-column justify-content-center p-3">
                        {documentChecking.rccm ? (
                          <>
                            <div className="spinner-border spinner-border-sm mb-2" style={{ color: '#4FC3F7' }}></div>
                            <small style={{ color: '#757575' }}>Chargement...</small>
                          </>
                        ) : (
                          <>
                            <i className="bi bi-file-earmark-text fs-2 mb-2" style={{ color: '#4FC3F7' }}></i>
                            <div>
                              <small style={{ color: '#757575', fontSize: '0.8rem' }}>RCCM</small>
                              <div className="mt-1">
                                <span className="badge" style={{ 
                                  backgroundColor: '#4FC3F7', 
                                  color: '#2E2E2E',
                                  fontSize: '0.7rem',
                                  padding: '2px 6px'
                                }}>
                                  {societe.rccm_societe || "Non défini"}
                                </span>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-8">
            <div className="card" style={{ 
              borderRadius: '12px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              border: 'none'
            }}>
              <div className="card-body">
                <h5 className="card-title" style={{ 
                  color: '#0B3C5D',
                  borderBottom: '2px solid #F5F7FA',
                  paddingBottom: '16px',
                  marginBottom: '24px'
                }}>
                  <i className="bi bi-info-circle me-2" style={{ color: '#1E88C8' }}></i>
                  Informations générales
                </h5>

                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-4">
                      <h6 style={{ color: '#0B3C5D', marginBottom: '16px', fontWeight: '600' }}>
                        <i className="bi bi-building me-2" style={{ color: '#1E88C8' }}></i>
                        Identification
                      </h6>
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <tbody>
                            <tr>
                              <td width="40%" style={{ color: '#757575', fontWeight: '500', paddingBottom: '12px' }}>Nom:</td>
                              <td style={{ color: '#2E2E2E', paddingBottom: '12px' }}>{societe.nom_societe}</td>
                            </tr>
                            <tr>
                              <td style={{ color: '#757575', fontWeight: '500' }}>Statut:</td>
                              <td style={{ color: '#2E2E2E' }}>{getStatutBadge(societe.statut_societe)}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h6 style={{ color: '#0B3C5D', marginBottom: '16px', fontWeight: '600' }}>
                        <i className="bi bi-card-text me-2" style={{ color: '#1E88C8' }}></i>
                        Informations légales
                      </h6>
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <tbody>
                            <tr>
                              <td width="40%" style={{ color: '#757575', fontWeight: '500', paddingBottom: '12px' }}>NIF:</td>
                              <td style={{ color: '#2E2E2E', paddingBottom: '12px' }}>
                                {societe.nif_societe ? (
                                  <span className="badge" style={{ 
                                    backgroundColor: '#0B3C5D', 
                                    color: 'white',
                                    fontSize: '0.75rem',
                                    padding: '4px 8px'
                                  }}>
                                    {societe.nif_societe}
                                  </span>
                                ) : (
                                  <span style={{ color: '#757575' }}>Non renseigné</span>
                                )}
                              </td>
                            </tr>
                            <tr>
                              <td style={{ color: '#757575', fontWeight: '500' }}>RCCM:</td>
                              <td style={{ color: '#2E2E2E' }}>
                                {societe.rccm_societe ? (
                                  <span className="badge" style={{ 
                                    backgroundColor: '#4FC3F7', 
                                    color: '#2E2E2E',
                                    fontSize: '0.75rem',
                                    padding: '4px 8px'
                                  }}>
                                    {societe.rccm_societe}
                                  </span>
                                ) : (
                                  <span style={{ color: '#757575' }}>Non renseigné</span>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-4">
                      <h6 style={{ color: '#0B3C5D', marginBottom: '16px', fontWeight: '600' }}>
                        <i className="bi bi-envelope me-2" style={{ color: '#1E88C8' }}></i>
                        Contact
                      </h6>
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <tbody>
                            <tr>
                              <td width="40%" style={{ color: '#757575', fontWeight: '500', paddingBottom: '12px' }}>Email:</td>
                              <td style={{ color: '#2E2E2E', paddingBottom: '12px' }}>
                                <a href={`mailto:${societe.email_societe}`} className="text-decoration-none" style={{ color: '#1E88C8' }}>
                                  {societe.email_societe}
                                </a>
                              </td>
                            </tr>
                            <tr>
                              <td style={{ color: '#757575', fontWeight: '500' }}>Téléphone:</td>
                              <td style={{ color: '#2E2E2E' }}>
                                {societe.tel_societe ? (
                                  <a href={`tel:${societe.tel_societe}`} className="text-decoration-none" style={{ color: '#2E2E2E' }}>
                                    {societe.tel_societe}
                                  </a>
                                ) : "-"}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div className="mb-4">
                      <h6 style={{ color: '#0B3C5D', marginBottom: '16px', fontWeight: '600' }}>
                        <i className="bi bi-geo-alt me-2" style={{ color: '#1E88C8' }}></i>
                        Adresse
                      </h6>
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <tbody>
                            <tr>
                              <td width="40%" style={{ color: '#757575', fontWeight: '500', paddingBottom: '12px' }}>Adresse:</td>
                              <td style={{ color: '#2E2E2E', paddingBottom: '12px' }}>{societe.address_societe || "-"}</td>
                            </tr>
                            <tr>
                              <td style={{ color: '#757575', fontWeight: '500' }}>Code postal:</td>
                              <td style={{ color: '#2E2E2E' }}>
                                {societe.code_postal_societe ? (
                                  <span className="badge" style={{ 
                                    backgroundColor: '#F5F7FA', 
                                    color: '#2E2E2E',
                                    fontSize: '0.75rem',
                                    padding: '4px 8px',
                                    border: '1px solid #e0e0e0'
                                  }}>
                                    {societe.code_postal_societe}
                                  </span>
                                ) : (
                                  <span style={{ color: '#757575' }}>Non renseigné</span>
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                <hr className="my-4" style={{ borderColor: '#F5F7FA' }} />
                <h6 style={{ color: '#0B3C5D', marginBottom: '20px', fontWeight: '600' }}>
                  <i className="bi bi-clock-history me-2" style={{ color: '#1E88C8' }}></i>
                  Historique
                </h6>
                <div className="row">
                  <div className="col-md-6">
                    <div className="card" style={{ 
                      backgroundColor: '#F5F7FA',
                      border: 'none',
                      borderRadius: '8px'
                    }}>
                      <div className="card-body py-3">
                        <small style={{ color: '#757575', fontSize: '0.85rem' }}>
                          <i className="bi bi-calendar-plus me-1" style={{ color: '#1E88C8' }}></i>
                          <strong style={{ color: '#2E2E2E' }}>Créée le:</strong> {formatDate(societe.created_at)}
                        </small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="card" style={{ 
                      backgroundColor: '#F5F7FA',
                      border: 'none',
                      borderRadius: '8px'
                    }}>
                      <div className="card-body py-3">
                        <small style={{ color: '#757575', fontSize: '0.85rem' }}>
                          <i className="bi bi-calendar-check me-1" style={{ color: '#1E88C8' }}></i>
                          <strong style={{ color: '#2E2E2E' }}>Modifiée le:</strong> {societe.updated_at ? formatDate(societe.updated_at) : "Jamais"}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Modal pour afficher les documents */}
      {imageModal.show && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: '12px', border: 'none' }}>
              <div className="modal-header" style={{ 
                backgroundColor: '#0B3C5D', 
                color: 'white',
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <h5 className="modal-title mb-0">{imageModal.title}</h5>
                <button 
                  type="button" 
                  className="btn-close btn-close-white" 
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body text-center" style={{ backgroundColor: '#F5F7FA', minHeight: '70vh' }}>
                {imageModal.url.toLowerCase().endsWith('.pdf') ? (
                  <iframe 
                    src={imageModal.url} 
                    title={imageModal.title}
                    className="w-100"
                    style={{ height: '70vh', border: 'none', borderRadius: '8px' }}
                  />
                ) : (
                  <img 
                    src={imageModal.url} 
                    alt={imageModal.title}
                    className="img-fluid"
                    style={{ 
                      maxHeight: '70vh',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 24 24"><path fill="%23ccc" d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"/></svg>';
                      e.target.alt = 'Document non disponible ou format non supporté';
                    }}
                  />
                )}
              </div>
              <div className="modal-footer" style={{ backgroundColor: '#F5F7FA' }}>
                <button 
                  type="button" 
                  className="btn"
                  onClick={closeModal}
                  style={{
                    color: '#2E2E2E',
                    borderColor: '#e0e0e0',
                    backgroundColor: 'transparent',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#F5F7FA';
                    e.target.style.borderColor = '#0B3C5D';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.borderColor = '#e0e0e0';
                  }}
                >
                  Fermer
                </button>
                <a 
                  href={imageModal.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn"
                  style={{
                    backgroundColor: '#0B3C5D',
                    borderColor: '#0B3C5D',
                    color: 'white',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    textDecoration: 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#1E88C8';
                    e.target.style.borderColor = '#1E88C8';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#0B3C5D';
                    e.target.style.borderColor = '#0B3C5D';
                  }}
                >
                  <i className="bi bi-box-arrow-up-right me-1"></i>
                  Ouvrir dans un nouvel onglet
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <style jsx="true">{`
        .cursor-pointer {
          cursor: pointer;
          transition: transform 0.2s;
        }
        
        .cursor-pointer:hover:not([disabled]) {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(11, 60, 93, 0.1);
        }
        
        .cursor-pointer[disabled] {
          opacity: 0.6;
          cursor: not-allowed;
        }
        
        .modal {
          background-color: rgba(0,0,0,0.5);
        }
        
        .modal.show {
          display: block;
        }
        
        .badge {
          border-radius: 6px;
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .d-flex.justify-content-center.gap-3 {
            gap: 16px !important;
          }
          
          .btn.rounded-circle {
            width: 44px !important;
            height: 44px !important;
          }
        }
      `}</style>
    </Layout>
  );
};

export default SocieteDetails;